import { css } from '@emotion/react';
import {
  InvoiceWithPayoutDescriptor,
  InvoiceModel,
  InvoiceStatus,
  FundingTypeDisplayValues,
  PaymentType,
} from '@frontend/api-invoices';
import { Chips } from '@frontend/chips';
import { formatDate } from '@frontend/date';
import { i18next } from '@frontend/i18n';
import { CardBrand } from '@frontend/payments-card-brand';
import { paymentTypeMap } from '@frontend/payments-hooks';
import {
  calculatePaidOrUnpaidInvoiceTotalWithSurcharge,
  calculateTotalFees,
  calculateTotalNetWithSurcharge,
  isPaidOrPendingInvoice,
} from '@frontend/payments-invoice-calculations';
import { CheckIcon, MinusIcon, TableColumnConfig } from '@frontend/design-system';
import { isCareCreditPayment } from '../../../hooks/care-credit-account-utils';
import { useToggleInvoiceRecorded } from '../../../hooks/use-mark-invoice-recorded';
import { formatCentsToCurrency } from '../../../utils/currency-helpers';
import { getPaymentModelBrand, GetLocationName } from '../../../utils/helpers';
import { InvoiceStatusChip } from '../../Invoices/InvoiceStatusChip';
import { PaymentsListContactName } from '../../PaymentsContactName/payments-list-contact-name';

type GenerateInvoiceColumnOptions = {
  showPayoutDescriptor: boolean;
  hasSurchargesEnabled?: boolean;
  careCreditLastFourMap?: Record<string, string | undefined>;
};

const isSubmittedPayment = (invoice: InvoiceModel): boolean =>
  !!invoice.payment &&
  (invoice.status !== InvoiceStatus.Unpaid ||
    (invoice.status === InvoiceStatus.Unpaid &&
      (invoice.payment.status === 'PENDING' || invoice.payment.status === 'SUCCEEDED')));

export const generateColumns =
  ({ showPayoutDescriptor, hasSurchargesEnabled, careCreditLastFourMap = {} }: GenerateInvoiceColumnOptions) =>
  (
    getLocationName: GetLocationName,
    showLocationName: boolean,
    forPrint = false
  ): TableColumnConfig<InvoiceWithPayoutDescriptor>[] =>
    [
      {
        id: 'recorded',
        Header: 'Recorded',
        accessor: (invoice) => invoice,
        cellConfig: {
          showHoverLabel: true,
          hoverLabelMessage: (_invoice, data) => {
            if (data.payment?.recordedAt) {
              return i18next.t('Click to mark unrecorded', { ns: 'payments' });
            } else if (
              data.status === InvoiceStatus.Paid ||
              data.status === InvoiceStatus.PartiallyPaid ||
              data.payment?.status === 'SUCCEEDED'
            ) {
              return i18next.t('Click to mark recorded', { ns: 'payments' });
            }
            return i18next.t('Click to view details', { ns: 'payments' });
          },
        },
        cellRenderer: (invoice) => <RecordedCell invoice={invoice} />,
        width: 100,
        cellAlign: 'center',
        disableSortBy: true,
      },
      {
        id: 'location',
        Header: 'Location Name',
        accessor: (invoice: InvoiceModel) => invoice.locationId,
        cellRenderer: (locationId: string) =>
          forPrint ? (
            getLocationName(locationId ?? '')
          ) : (
            <Chips.LocationChip
              css={css`
                max-width: none;
              `}
            >
              {getLocationName(locationId ?? '')}
            </Chips.LocationChip>
          ),
        disableSortBy: true,
        omit: !showLocationName,
      },
      {
        id: 'person',
        Header: 'Contact Name',
        minWidth: 250,
        width: 250,
        maxWidth: 400,
        disableSortBy: true,
        accessor: (invoice) => invoice,
        cellRenderer: (invoice: InvoiceWithPayoutDescriptor) => (
          <PaymentsListContactName id={invoice.person.id} name={invoice.person.name} locationId={invoice.locationId} />
        ),
      },
      {
        id: 'status',
        Header: 'Status',
        accessor: (invoice) => invoice,
        cellRenderer: (invoice: InvoiceWithPayoutDescriptor) => <InvoiceStatusChip invoice={invoice} />,
        width: 100,
        disableSortBy: true,
      },
      {
        id: 'billedAmount',
        Header: 'Amount',
        accessor: (invoice) => (invoice.billedAmount ? formatCentsToCurrency(invoice.billedAmount) : ''),
        cellAlign: 'right',
        width: 120,
      },
      {
        id: 'billedAt',
        Header: 'Creation Date',
        width: 120,
        accessor: (invoice) => (invoice.billedAt ? formatDate(invoice.billedAt, 'MMM D, YYYY') : ''),
      },
      {
        id: 'surchargeAmount',
        Header: 'Surcharge Amount',
        width: 120,
        omit: !hasSurchargesEnabled,
        accessor: (invoice) =>
          invoice.appliedSurchargeAmount && isPaidOrPendingInvoice(invoice)
            ? formatCentsToCurrency(invoice.appliedSurchargeAmount)
            : '',
      },
      {
        id: 'paidAmount',
        Header: 'Paid Amount',
        accessor: (invoice) =>
          invoice.payment && isSubmittedPayment(invoice)
            ? formatCentsToCurrency(calculatePaidOrUnpaidInvoiceTotalWithSurcharge(invoice))
            : '',
        width: 120,
        cellAlign: 'right',
      },
      {
        id: 'paidAt',
        Header: 'Paid Date',
        accessor: (invoice) =>
          invoice.payment && isSubmittedPayment(invoice) && invoice.payment.paidAt
            ? formatDate(invoice.payment.paidAt, 'MMM D, YYYY')
            : '',
        width: 120,
      },
      {
        id: 'fee',
        Header: 'Fees',
        accessor: (invoice) =>
          invoice?.payment && isSubmittedPayment(invoice) && formatCentsToCurrency(calculateTotalFees(invoice)),
        width: 100,
        disableSortBy: true,
        cellAlign: 'right',
      },
      {
        id: 'net',
        Header: 'Net',
        accessor: (invoice) =>
          invoice?.payment &&
          isSubmittedPayment(invoice) &&
          formatCentsToCurrency(calculateTotalNetWithSurcharge(invoice)),
        width: 100,
        disableSortBy: true,
        cellAlign: 'right',
      },
      {
        id: 'origin',
        Header: 'Collection Method',
        accessor: (invoice) => invoice?.payment && paymentTypeMap[invoice.payment.origin],
        width: 160,
        disableSortBy: true,
        cellConfig: {
          showHoverLabel: true,
        },
      },
      {
        id: 'pay_method',
        Header: 'Payment Method',
        accessor: (invoice) => {
          const label =
            invoice.payment?.paymentType === PaymentType.CardPresent ? 'Card' : invoice.payment?.paymentType ?? '';
          return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
        },
        disableSortBy: true,
        width: 150,
      },
      {
        id: 'card_type',
        Header: 'Card Type',
        accessor: (invoice) => {
          if (isCareCreditPayment(invoice.payment)) {
            return i18next.t('Credit', { ns: 'payments' });
          }

          return invoice.payment?.paymentDetails?.fundingType
            ? FundingTypeDisplayValues[invoice.payment.paymentDetails.fundingType]
            : '';
        },
        disableSortBy: true,
        width: 100,
      },
      {
        id: 'paymentMethod',
        Header: 'Card Brand',
        accessor: (invoice) => invoice,
        cellRenderer: (invoice: InvoiceModel) => {
          const last4 = isCareCreditPayment(invoice.payment)
            ? careCreditLastFourMap[invoice.id]
            : invoice.payment?.paymentDetails.lastFour;

          return <CardBrand brand={getPaymentModelBrand(invoice.payment)} last4={last4} />;
        },
        maxWidth: 180,
        disableSortBy: true,
      },
      {
        id: 'providerName',
        Header: 'Provider Name',
        accessor: (invoice: InvoiceModel) => invoice.providerName,
        disableSortBy: true,
      },
      {
        id: 'patientId',
        Header: 'Customer Id',
        accessor: (invoice) => invoice?.person?.id,
        width: 375,
        disableSortBy: true,
      },
      {
        id: 'payoutDescriptor',
        Header: 'Payout Descriptor',
        accessor: (invoice) => invoice.payoutDescriptor ?? '',
        disableSortBy: true,
        omit: !showPayoutDescriptor,
      },
    ];

const RecordedCell = ({ invoice }: { invoice: InvoiceModel }) => {
  const { toggleRecorded } = useToggleInvoiceRecorded();

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      `}
      onClick={(e) => {
        if (
          invoice.payment?.recordedAt ||
          invoice.status === InvoiceStatus.Paid ||
          invoice.status === InvoiceStatus.PartiallyPaid ||
          invoice.payment?.status === 'SUCCEEDED'
        ) {
          e.stopPropagation();
          toggleRecorded({
            data: invoice,
            paymentId: invoice.payment?.paymentId,
            recordedAt: invoice.payment?.recordedAt,
          });
        }
        return false;
      }}
    >
      {invoice.payment && invoice.payment.recordedAt ? <CheckIcon color='success' /> : <MinusIcon color='default' />}
    </div>
  );
};
